<template>
  <section class="stock-chart-lookup" :class="{ compact }" aria-label="个股日K查询">
    <details class="stock-chart-lookup-disclosure">
    <summary class="stock-chart-lookup-label"><strong>{{ compact ? '日K查询' : '查询个股日K' }} <span aria-hidden="true">↗</span></strong><span v-if="!compact">名称 / 代码 · MA均线</span></summary>
    <div class="stock-chart-search" @focusout="onSearchBlur">
      <form class="stock-chart-search-form" @submit.prevent="submitSearch">
        <input
          ref="searchInput"
          v-model.trim="query"
          type="search"
          role="combobox"
          aria-label="输入股票代码或名称查询日K"
          aria-autocomplete="list"
          aria-controls="stock-chart-suggestions"
          :aria-expanded="showSuggestions && suggestions.length > 0 ? 'true' : 'false'"
          :aria-activedescendant="suggestionIndex >= 0 ? `stock-chart-option-${suggestionIndex}` : null"
          placeholder="如 603200 / 上海洗霸"
          autocomplete="off"
          @input="onSearchInput"
          @focus="showSuggestions = true"
          @keydown.down.prevent="moveSuggestion(1)"
          @keydown.up.prevent="moveSuggestion(-1)"
          @keydown.esc.stop="showSuggestions = false"
        >
        <button type="submit">查看日K ↗</button>
      </form>
      <ul v-if="showSuggestions && suggestions.length" id="stock-chart-suggestions" class="stock-chart-suggestions" role="listbox">
        <li v-for="(item, index) in suggestions" :id="`stock-chart-option-${index}`" :key="item.code" role="option" :aria-selected="suggestionIndex === index ? 'true' : 'false'">
          <button type="button" :class="{ active: suggestionIndex === index }" @mousedown.prevent @click="chooseSuggestion(item)">
            <strong>{{ item.name || item.code }}</strong><code>{{ item.code }}</code><small>{{ item.industry || item.market }}</small>
          </button>
        </li>
      </ul>
      <p v-if="searchError || searchLoading" class="stock-chart-search-note" role="status">{{ searchLoading ? '正在查找…' : searchError }}</p>
    </div>
    </details>
    <div v-if="isOpen" class="stock-chart-overlay" @click.self="close">
      <section ref="dialog" class="stock-chart-dialog" role="dialog" aria-modal="true" aria-labelledby="stock-chart-title" tabindex="-1" @keydown="onDialogKeydown">
        <header class="stock-chart-header">
          <div><p>KLINE / {{ periodLabel }}</p><h3 id="stock-chart-title">{{ stock.name || stock.code }} <code>{{ stock.code }}</code></h3></div>
          <button ref="closeButton" class="stock-chart-close" type="button" aria-label="关闭日K图" @click="close">×</button>
        </header>
        <div class="stock-chart-periods" aria-label="K线与缠论周期">
          <button v-for="option in periodOptions" :key="option.value" type="button" :class="{ active: period === option.value }" :aria-pressed="period === option.value ? 'true' : 'false'" @click="changePeriod(option.value)">{{ option.label }}</button>
          <button type="button" :aria-pressed="showPointLabels ? 'true' : 'false'" @click="showPointLabels = !showPointLabels">{{ showPointLabels ? '隐藏' : '显示' }}点位标注</button>
          <small>仅已完成K线 · 各周期独立分析</small>
        </div>
        <div v-if="loading" class="stock-chart-message" role="status">正在读取{{ periodLabel }}和均线…</div>
        <div v-else-if="error" class="stock-chart-message" role="alert">{{ error }} <button type="button" @click="openStock(stock)">重试</button></div>
        <template v-else-if="bars.length">
          <div class="stock-chart-toolbar">
            <span>{{ activeBar.trade_date }} <small>{{ activeIndex < 0 ? '最近K线' : '历史K线' }}</small></span>
            <div class="stock-chart-ranges" aria-label="日K显示范围">
              <button v-for="count in [60, 120, 250]" :key="count" type="button" :class="{ active: visibleCount === count }" :aria-pressed="visibleCount === count ? 'true' : 'false'" @click="setRange(count)">近{{ count }}根</button>
            </div>
          </div>
          <div class="stock-chart-ohlc">
            <span>开 <b>{{ price(activeBar.open) }}</b></span><span>高 <b>{{ price(activeBar.high) }}</b></span>
            <span>低 <b>{{ price(activeBar.low) }}</b></span><span>收 <b :class="activeBar.close >= activeBar.open ? 'stock-chart-up' : 'stock-chart-down'">{{ price(activeBar.close) }}</b></span>
            <span>涨跌 <b :class="Number(activeBar.change_pct) >= 0 ? 'stock-chart-up' : 'stock-chart-down'">{{ percent(activeBar.change_pct) }}</b></span>
          </div>
          <div class="stock-chart-ma-values" aria-label="所选日期的均线数值" aria-live="polite">
            <div v-for="ma in maStyles" :key="ma.period" :style="{ '--ma-color': ma.color }" :title="maHint(ma.period)">
              <span>MA{{ ma.period }}</span><strong>{{ price(activeBar[`ma${ma.period}`]) }}</strong>
            </div>
          </div>
          <details v-if="result.bottom_repair && result.bottom_repair.enabled" class="stock-repair-detail">
            <summary><strong>筑底修复 · {{ activeRepair.stage_label || '数据不足' }}</strong><span>{{ activeRepair.evidence_count === null || activeRepair.evidence_count === undefined ? '--' : activeRepair.evidence_count }}/4 项证据</span><small>{{ result.bottom_repair.freshness === 'stale' ? '历史日线，等待补齐' : '已完成日线' }}</small></summary>
            <div class="stock-repair-values">
              <div><span>地量锚点</span><strong>{{ activeRepair.anchor_date || '--' }}</strong></div>
              <div v-for="line in repairReadouts" :key="line.key"><span>{{ line.label }}</span><strong>{{ line.value === null ? '--' : pointPrice(line.value) }}</strong><small>{{ line.detail }}</small></div>
              <div><span>风险距离 / 量能倍数</span><strong>{{ percent(activeRepair.risk_distance_pct) }} / {{ price(activeRepair.volume_multiple_5) }}倍</strong></div>
            </div>
            <ul class="stock-repair-evidence">
              <li v-for="item in activeRepair.evidence || []" :key="item.id"><strong>{{ item.label }}</strong><span>{{ item.status === 'passed' ? '满足' : item.status === 'unavailable' ? '数据不足 / 待端点确认' : '待满足' }}</span><small>{{ item.date || '--' }}</small></li>
            </ul>
            <p v-if="activeRepair.divergence">背离低点 {{ activeRepair.divergence.first_date }} → {{ activeRepair.divergence.second_date }} · {{ activeRepair.divergence.confirmed_at }} 才确认</p>
            <p>本地存储 {{ result.bottom_repair.stored_history_count === undefined ? '--' : result.bottom_repair.stored_history_count }} 根 · 所选日指标连续 {{ activeRepair.history_segment_count === undefined ? '--' : activeRepair.history_segment_count }} 根 / 至少 {{ activeRepair.history_required_count || 60 }} 根</p>
            <p v-if="activeRepair.last_reset">最近分段 {{ activeRepair.last_reset.trade_date || '--' }} · {{ resetLabel(activeRepair.last_reset.reason) }}<span v-if="activeRepair.last_reset.previous_source">（{{ activeRepair.last_reset.previous_source }} → {{ activeRepair.last_reset.source }}）</span></p>
            <p v-if="!stock.isIndex">个股修复确认还需DIF底背离、收盘站上两条修复线及放量≥前5日均量{{ repairParameters.volume_multiple }}倍；指数使用结构参考模式，不要求该放量倍数。</p>
            <p>修复确认后的后续交易日，最低价跌破失效参考线，或收盘跌回锚定均价线下方，均判为修复失效。失效参考取结构低点下方{{ repairParameters.stop_buffer_pct }}%，并非买价下方；过期或失效结构仅供历史观察。</p>
            <p v-for="issue in result.bottom_repair.issues || []" :key="issue">{{ issue }}</p>
          </details>
          <div v-if="result.bottom_repair && result.bottom_repair.enabled" class="stock-repair-toggle">
            <button type="button" :aria-pressed="showRepair ? 'true' : 'false'" @click="showRepair = !showRepair">{{ showRepair ? '隐藏' : '显示' }}修复参考线</button>
            <small>{{ activeIndex < 0 ? '最新日线' : '所选日线' }} {{ activeBar.trade_date }} · {{ activeRepair.stage_label || '数据不足' }} · 圆点为当日线值，右侧注解由引线定位</small>
          </div>
          <p v-if="result.bottom_repair && result.bottom_repair.enabled && showRepair && !repairReadouts.some(line => line.value !== null)" class="stock-repair-empty">所选日暂无修复参考线，需等待完整日线或结构端点确认。</p>
          <details class="stock-repair-detail stock-chan-detail">
            <summary><strong>缠论 · {{ chanSummary }}</strong><small>{{ chanLoading ? '读取中…' : chanStateLabel }} · {{ periodLabel }}</small></summary>
            <p>{{ chan.message || chanError || 'chan.py 结构观察，候选会随新增K线变化。' }} <button type="button" :disabled="chanLoading" @click="loadChan">刷新缠论</button></p>
            <p>连续 {{ chan.history_segment_count || 0 }} 根 · 结构截至 {{ chan.trade_date || '--' }} · {{ result.price_basis || '--' }}</p>
            <p v-for="issue in chan.issues || []" :key="issue">{{ issue }}</p>
            <p v-if="chanHistorical">当前查看 {{ activeBar.trade_date }} 的回放记录；已隐藏事后形成的图层。返回最新K线查看当前结构。</p>
            <p v-else-if="scenarioModel && scenarioModel.center">中枢 {{ pointPrice(scenarioModel.center.low) }} — {{ pointPrice(scenarioModel.center.high) }} · {{ scenarioModel.center.confirmed ? '已确认' : '候选' }}<span v-if="scenarioModel.center.divergence_candidate"> · {{ scenarioModel.center.divergence_side === 'bottom' ? '底' : '顶' }}背驰候选，MACD面积比 {{ price(scenarioModel.center.divergence_ratio) }}</span></p>
            <div class="stock-chan-layers" aria-label="缠论图层">
              <label v-for="layer in chanLayerOptions" :key="layer.key"><input v-model="chanLayers[layer.key]" type="checkbox">{{ layer.label }}</label>
            </div>
            <ul class="stock-chan-signals">
              <li v-for="signal in activeChanSignals.slice(-8)" :key="signal.id">{{ signalLabel(signal) }} · {{ signal.status === 'confirmed' ? '已确认' : signal.status === 'invalidated' ? '已失效' : '候选' }} · 结构 {{ signal.structure_date }} · 回放发现 {{ signal.first_seen_at || '--' }} · 回放确认 {{ signal.confirmed_at || '--' }}<span v-if="signal.invalidated_at"> · 失效 {{ signal.invalidated_at }}</span></li>
            </ul>
            <p v-if="!activeChanSignals.length">所选日暂无买卖点记录；不表示数据失败。</p>
            <p>实线为已确认结构，虚线为发展中结构。回放日期不代表历史当天已发出提醒；仅供形态观察。</p>
          </details>
          <section v-if="scenarioModel" class="stock-chan-scenario" aria-label="缠论区间与情景点位">
            <header><strong>{{ scenarioModel.message }}</strong><small>{{ periodLabel }} · 截至 {{ scenarioModel.as_of }}</small></header>
            <p v-if="scenarioModel.center">{{ scenarioModel.center.confirmed ? '已确认' : '候选' }}中枢 {{ pointPrice(scenarioModel.center.low) }} — {{ pointPrice(scenarioModel.center.high) }}</p>
            <div class="stock-chan-references">
              <div><span>支撑参考</span><p v-for="ref in scenarioModel.support" :key="`s-${ref.price}`">{{ pointPrice(ref.price) }} <small>{{ ref.label }}</small></p><p v-if="!scenarioModel.support.length">暂无已有结构参考</p></div>
              <div><span>压力参考</span><p v-for="ref in scenarioModel.resistance" :key="`r-${ref.price}`">{{ pointPrice(ref.price) }} <small>{{ ref.label }}</small></p><p v-if="!scenarioModel.resistance.length">暂无已有结构参考</p></div>
            </div>
            <details v-if="scenarioModel.scenarios.length" class="stock-chan-conditions">
              <summary>展开突破、回踩与失效条件</summary>
              <article v-for="item in scenarioModel.scenarios" :key="item.id" :class="{ 'is-current': item.active }">
                <strong>{{ item.label }}{{ item.active ? ' · 当前所处情景' : '' }}{{ item.status === 'candidate' ? ' · 候选中枢' : '' }}</strong>
                <p>{{ item.condition }}</p><p>{{ item.reference_text }}</p><p>失效：{{ item.invalidation }}</p><small v-if="item.note">{{ item.note }}</small>
              </article>
            </details>
            <small>{{ scenarioModel.note }} 图内仅显示当前纵轴范围内的参考位，完整数值见上方。</small>
          </section>
          <p v-else-if="chanHistorical" class="stock-chan-hint">历史回放已隐藏最新情景点位。<button type="button" @click="activeIndex = -1">返回最近K线</button></p>
          <p v-else class="stock-chan-hint">{{ chan.message || chanError || '等待缠论计算结果；暂无可用区间。' }} <span v-if="result.refresh_error">采集：{{ result.refresh_error }}</span></p>
          <div ref="chartScroll" class="stock-chart-scroll" tabindex="0" aria-label="K线图，左右方向键查看价格与均线" @keydown.left.prevent="moveBar(-1)" @keydown.right.prevent="moveBar(1)" @keydown.home.prevent="activeIndex = 0" @keydown.end.prevent="activeIndex = -1">
            <svg class="stock-chart-svg" :class="{ 'has-repair-analysis': result.bottom_repair && result.bottom_repair.enabled }" :viewBox="`0 0 ${geometry.width} ${geometry.height}`" role="img" aria-labelledby="stock-chart-svg-title stock-chart-svg-desc" @mouseleave="activeIndex = -1">
              <title id="stock-chart-svg-title">{{ stock.name || stock.code }} {{ periodLabel }}与MA5、10、13、17、55</title>
              <desc id="stock-chart-svg-desc">红涨绿跌，下方为成交量。移动鼠标或点击K线查看当日价格和均线，也可用左右方向键逐日查看。</desc>
              <text :x="geometry.left" :y="16" class="stock-chart-axis">{{ stock.isIndex ? '点数（点）' : '价格（元）' }}</text>
              <g v-for="tick in geometry.ticks" :key="tick.y" class="stock-chart-grid">
                <line :x1="geometry.left" :x2="geometry.right" :y1="tick.y" :y2="tick.y" />
                <text :x="geometry.left - 10" :y="tick.y + 4" text-anchor="end">{{ price(tick.value) }}</text>
              </g>
              <text :x="geometry.left" :y="geometry.volumeTop - 8" class="stock-chart-axis">成交量（相对高度）</text>
              <g v-for="center in geometry.chanCenters" :key="`zs-${center.id}`" pointer-events="none">
                <rect :x="center.x" :y="center.y" :width="center.width" :height="center.height" fill="#287eab" fill-opacity=".08" stroke="#287eab" :stroke-dasharray="center.is_sure ? null : '5 4'"><title>中枢 {{ pointPrice(center.low) }} — {{ pointPrice(center.high) }} · {{ center.is_sure ? '已确认' : '候选' }}</title></rect>
                <text v-if="showPointLabels && center.width >= 160" :x="center.x + 4" :y="Math.max(geometry.top + 12, center.y - 4)" class="stock-chart-center-label">中枢 {{ pointPrice(center.low) }}—{{ pointPrice(center.high) }}</text>
              </g>
              <g v-for="candle in geometry.candles" :key="candle.date" :fill="candle.color" :stroke="candle.color">
                <line :x1="candle.x" :x2="candle.x" :y1="candle.high" :y2="candle.low" />
                <rect :x="candle.x - geometry.candleWidth / 2" :y="candle.bodyY" :width="geometry.candleWidth" :height="candle.bodyHeight" stroke="none" />
                <rect :x="candle.x - geometry.candleWidth / 2" :y="candle.volumeY" :width="geometry.candleWidth" :height="candle.volumeHeight" opacity=".45" stroke="none" />
              </g>
              <g v-for="line in geometry.maLines" :key="line.period" fill="none" :stroke="line.color" stroke-width="1.6" stroke-linejoin="round">
                <polyline v-for="(points, index) in line.segments" :key="index" :points="points" />
              </g>
              <g v-for="line in geometry.repairLines" :key="line.key" fill="none" :stroke="line.color" stroke-width="1.8" stroke-dasharray="5 3">
                <polyline v-for="(points, index) in line.segments" :key="index" :points="points"><title>{{ line.label }}</title></polyline>
              </g>
              <line v-for="line in geometry.chanLines" :key="line.key" :x1="line.x1" :x2="line.x2" :y1="line.y1" :y2="line.y2" :stroke="line.kind === 'segment' ? '#a74779' : '#147d76'" :stroke-width="line.kind === 'segment' ? 2.5 : 1.5" :stroke-dasharray="line.is_sure ? null : '5 4'" pointer-events="none" />
              <g v-for="mark in geometry.chanMarks" :key="mark.key" pointer-events="none">
                <circle :cx="mark.x" :cy="mark.y" :r="mark.signal ? 4 : 2.5" :fill="mark.buy ? '#c34e47' : '#287eab'" :fill-opacity="mark.is_sure ? 1 : .45"><title>{{ mark.label || '分型' }} {{ pointPrice(mark.price) }}</title></circle>
              </g>
              <g v-for="point in geometry.pointLabels" :key="`point-${point.key}`" class="stock-chart-point-label" pointer-events="none">
                <circle :cx="point.x" :cy="point.y" r="2.5" :fill="point.color" />
                <line :x1="point.x" :y1="point.y" :x2="point.labelX" :y2="point.labelY - 4" :stroke="point.color" stroke-width=".7" />
                <text :x="point.labelX" :y="point.labelY" :fill="point.color">{{ point.text }}</text>
              </g>
              <g v-for="ref in geometry.referenceLines" :key="ref.id" class="stock-chart-reference" pointer-events="none">
                <line :x1="geometry.left" :x2="geometry.right" :y1="ref.y" :y2="ref.y" :stroke="ref.color" :stroke-dasharray="ref.confirmed ? '6 3' : '2 4'" stroke-width=".8" />
                <line :x1="geometry.right - 5" :x2="geometry.right - 155" :y1="ref.y" :y2="ref.labelY - 3" :stroke="ref.color" stroke-width=".6" />
                <text :x="geometry.right - 158" :y="ref.labelY" text-anchor="end" :fill="ref.color">{{ ref.label }} {{ pointPrice(ref.price) }}{{ ref.confirmed ? '' : '（候选）' }}</text>
              </g>
              <g v-for="label in geometry.repairLabels" :key="`repair-${label.key}`" class="stock-chart-repair-label" pointer-events="none">
                <circle :cx="label.x" :cy="label.y" r="3.5" :fill="label.color" stroke="#fffaf0" stroke-width="1" />
                <path :d="`M ${label.x} ${label.y} L ${geometry.right + 6} ${label.y} L ${label.labelX - 5} ${label.labelY + 4}`" fill="none" :stroke="label.color" stroke-width=".8" />
                <rect :x="label.labelX - 2" :y="label.labelY - 14" width="212" height="39" rx="3" fill="#fffaf0" fill-opacity=".95" />
                <text :x="label.labelX" :y="label.labelY" :fill="label.color">{{ label.label }} {{ pointPrice(label.value) }}</text>
                <text :x="label.labelX" :y="label.labelY + 17" class="stock-chart-repair-meaning">{{ label.meaning }}</text>
                <title>{{ activeBar.trade_date }} · {{ label.detail }}</title>
              </g>
              <g v-for="tick in geometry.dates" :key="tick.index" class="stock-chart-axis">
                <text :x="tick.x" :y="geometry.height - 10" :text-anchor="tick.anchor">{{ tick.label }}</text>
              </g>
              <g v-if="geometry.candles.length && activeIndex >= 0" class="stock-chart-crosshair">
                <line :x1="geometry.candles[activeIndex].x" :x2="geometry.candles[activeIndex].x" :y1="geometry.top" :y2="geometry.volumeBottom" />
                <line :x1="geometry.left" :x2="geometry.right" :y1="geometry.candles[activeIndex].close" :y2="geometry.candles[activeIndex].close" />
              </g>
              <g v-if="geometry.selectedPrice" class="stock-chart-selected-price" pointer-events="none">
                <line :x1="geometry.left" :x2="geometry.right" :y1="geometry.selectedPrice.y" :y2="geometry.selectedPrice.y" stroke="#8b8274" stroke-dasharray="3 4" stroke-width=".7" />
                <rect :x="geometry.right - 120" :y="geometry.selectedPrice.y - 15" width="118" height="18" rx="3" fill="#fffaf0" stroke="#b8ac97" />
                <text :x="geometry.right - 5" :y="geometry.selectedPrice.y - 2" text-anchor="end" fill="#65594a" font-size="10">{{ activeIndex < 0 ? '最新收盘' : '所选收盘' }} {{ pointPrice(activeBar.close) }}</text>
              </g>
              <rect v-for="(candle, index) in geometry.candles" :key="`hit-${candle.date}`" :x="candle.x - geometry.step / 2" :y="geometry.top" :width="geometry.step" :height="geometry.volumeBottom - geometry.top" fill="transparent" @mouseenter="activeIndex = index" @click="activeIndex = index" />
            </svg>
          </div>
          <footer class="stock-chart-footer">
            <span>移动 / 点击K线查看MA值 · 共{{ displayBars.length }}根{{ periodLabel }} · MA为当前周期收盘价简单平均</span>
            <span>本地缓存 · 截至 {{ bars[bars.length - 1].trade_date }} · {{ result.price_basis || '沿用本地入库价格口径' }}</span>
            <span v-if="result.refresh_error">分钟刷新：{{ result.refresh_error }}</span>
            <span v-if="result.adjustment_reference_date">价格按 {{ result.adjustment_reference_date }} 前复权；成交量与成交额保留原始值</span>
            <span v-if="maPendingText">{{ activeBar.trade_date }} · {{ maPendingText }}</span>
            <span v-if="result.message">{{ result.message }}</span>
            <span>历史不足时均线显示 --；除权除息前后请核对复权口径。</span>
          </footer>
        </template>
        <div v-else class="stock-chart-message" role="status">{{ result.status === 'pending' ? '分钟OHLC正在后台串行采集，稍后自动刷新…' : result.message || '本地暂无该周期K线，请先补足历史。' }}<p v-if="result.refresh_error">{{ result.refresh_error }}</p><button type="button" @click="openStock(stock)">重新读取</button></div>
      </section>
    </div>
  </section>
</template>

<script>
const API_BASE = (process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
const MA_STYLES = [
  { period: 5, color: '#a36914' }, { period: 10, color: '#a74779' },
  { period: 13, color: '#287eab' }, { period: 17, color: '#63852a' },
  { period: 55, color: '#7855b2' }
]
const REPAIR_STYLES = [
  { key: 'avwap', color: '#147d76', label: '锚定均价', meaning: '地量锚点以来的量加权成本', detail: 'AVWAP：从地量锚点累计，以每日典型价（高+低+收）/3按成交量加权；站回线上表示收复这一成本参考。' },
  { key: 'fan_05', color: '#cf7b19', label: '0.5扇线', meaning: '收盘突破，观察下跌趋势修复', detail: '从结构前高到已确认低点的下跌斜率取一半并向后延伸；收盘站上仅是修复证据之一，不是涨幅目标。' },
  { key: 'reference_stop', color: '#8b6666', label: '失效参考', meaning: '结构低点下方的风险参考', detail: '结构低点按配置比例向下缓冲；修复确认后的后续交易日，最低价跌破此线会判为失效，收盘跌回锚定均价下方也会失效。' }
]

export default {
  props: { compact: { type: Boolean, default: false } },
  name: 'StockDailyChart',
  data () {
    return {
      query: '', suggestions: [], suggestionIndex: -1, showSuggestions: false,
      searchLoading: false, searchError: '', searchRequestId: 0,
      searchTimer: null, searchController: null,
      isOpen: false, stock: {}, loading: false, error: '', result: {}, bars: [],
      klineRequestId: 0, klineController: null, klineTimer: null, visibleCount: 120, activeIndex: -1, period: 'day',
      returnFocus: null, showRepair: true, showPointLabels: true,
      chan: {}, chanError: '', chanLoading: false, chanRequestId: 0, chanController: null, chanTimer: null, chanPollCount: 0,
      chanLayers: { strokes: true, centers: true, segments: false, fractals: false, signals: true }
    }
  },
  computed: {
    periodOptions () { return [{ value: 'day', label: '日线' }, { value: 'week', label: '周线' }, ...[1, 5, 15, 30, 60].map(n => ({ value: `${n}m`, label: `${n}分` }))] },
    periodLabel () { return ({ day: '日线', week: '周线' })[this.period] || `${this.period.replace('m', '')}分钟` },
    chanLayerOptions () { return [{ key: 'strokes', label: '笔' }, { key: 'centers', label: '中枢' }, { key: 'segments', label: '线段' }, { key: 'fractals', label: '分型' }, { key: 'signals', label: '买卖点' }] },
    chanHistorical () { return !!(this.bars.length && this.activeBar.trade_date !== this.bars[this.bars.length - 1].trade_date) },
    chanSnapshot () { return (this.chan.timeline || []).find(row => row.trade_date === this.activeBar.trade_date) },
    chanSummary () {
      if (this.chanHistorical) { const snap = this.chanSnapshot; return snap && snap.segment_direction ? `${snap.segment_direction === 'up' ? '上行' : '下行'}段${snap.segment_confirmed ? '已确认' : '发展中'} · 当日回放` : '当日暂无成段结构' }
      return (this.scenarioModel && this.scenarioModel.message) || this.chan.summary || this.chanStateLabel
    },
    chanStateLabel () { return ({ ok: '计算正常', partial: '候选结构', pending: '后台计算中', runtime_missing: 'Python解释器不可用', missing: '该周期K线未入库', stale: '等待该周期完整K线', unverified: '复权口径待核验', insufficient: '连续历史不足', disabled: '已关闭', error: '采集或计算异常' })[this.chan.status] || (this.chanError ? '读取失败' : '等待读取') },
    activeChanSignals () { return this.chanHistorical ? (this.chanSnapshot || {}).signals || [] : this.chan.signals || [] },
    chanAligned () {
      return !this.chanHistorical && this.bars.length > 0 && ['ok', 'partial'].includes(this.chan.status) &&
        this.chan.trade_date === this.bars[this.bars.length - 1].trade_date &&
        this.chan.adjustment_reference_date === this.result.adjustment_reference_date &&
        (!this.chan.level || this.chan.level === this.period)
    },
    scenarioModel () {
      const model = this.chan.scenario
      return this.chanAligned && model && model.available && model.as_of === this.chan.trade_date && model.level === this.period ? model : null
    },
    chanShapes () {
      const empty = { lines: [], centers: [], marks: [] }
      const bars = this.displayBars
      if (!this.chanAligned) return empty
      const timeline = (this.chan.timeline || []).map(row => row.trade_date)
      const first = timeline.indexOf(bars[0].trade_date); const last = timeline.indexOf(bars[bars.length - 1].trade_date)
      if (first < 0 || last < first) return empty
      const finite = value => value != null && Number.isFinite(Number(value)) && Number(value) > 0
      const lines = []
      for (const kind of ['strokes', 'segments']) {
        if (!this.chanLayers[kind]) continue
        for (const line of this.chan[kind] || []) {
          const a = timeline.indexOf(line.start_date); const b = timeline.indexOf(line.end_date)
          if (a < 0 || b <= a || b < first || a > last || !finite(line.start_price) || !finite(line.end_price)) continue
          const start = Math.max(first, a); const end = Math.min(last, b)
          const value = i => Number(line.start_price) + (Number(line.end_price) - Number(line.start_price)) * (i - a) / (b - a)
          lines.push({ ...line, key: `${kind}-${line.id}`, kind: kind === 'segments' ? 'segment' : 'stroke', a: start - first, b: end - first, p1: value(start), p2: value(end) })
        }
      }
      const centers = this.chanLayers.centers ? (this.chan.centers || []).filter(c => c.end_date >= bars[0].trade_date && c.start_date <= bars[bars.length - 1].trade_date && finite(c.low) && finite(c.high)).map(c => ({ ...c, a: Math.max(first, timeline.indexOf(c.start_date)) - first, b: Math.min(last, timeline.indexOf(c.end_date)) - first })) : []
      const marks = []
      for (const [kind, enabled] of [['fractals', this.chanLayers.fractals], ['signals', this.chanLayers.signals]]) {
        if (!enabled) continue
        for (const mark of this.chan[kind] || []) {
          const i = bars.findIndex(b => b.trade_date === (mark.date || mark.structure_date))
          if (i < 0 || mark.status === 'invalidated' || !finite(mark.price)) continue
          marks.push({ ...mark, i, key: `${kind}-${mark.id || mark.date}`, signal: kind === 'signals', buy: kind === 'signals' ? mark.side === 'buy' : mark.type === 'bottom', is_sure: kind === 'signals' ? mark.status === 'confirmed' : mark.is_sure, label: kind === 'signals' ? this.signalLabel(mark) : mark.type === 'bottom' ? '底分型' : '顶分型' })
        }
      }
      return { lines, centers, marks }
    },
    maStyles () { return MA_STYLES },
    displayBars () { return this.bars.slice(-this.visibleCount) },
    activeBar () { return this.displayBars[this.activeIndex] || this.displayBars[this.displayBars.length - 1] || {} },
    activeRepair () { return this.activeBar.bottom_repair || {} },
    repairParameters () { return (this.result.bottom_repair || {}).parameters || {} },
    repairReadouts () {
      return REPAIR_STYLES.map(line => {
        const raw = this.activeRepair[line.key]
        const value = raw !== null && raw !== undefined && raw !== '' && Number.isFinite(Number(raw)) && Number(raw) > 0 ? Number(raw) : null
        return { ...line, value }
      })
    },
    maPendingText () {
      return MA_STYLES.filter(ma => (this.activeBar.ma_status || {})[`ma${ma.period}`]?.status === 'insufficient_history')
        .map(ma => this.maHint(ma.period)).join('；')
    },
    geometry () {
      const width = 1000; const height = 410
      // Reserve a gutter for repair explanations, away from candles, Chan
      // references and the close badge. No last-valid-value carry forward.
      const hasRepairLabels = this.showRepair && this.showPointLabels && this.repairReadouts.some(line => line.value !== null)
      const left = 68; const right = hasRepairLabels ? 744 : 976; const top = 18; const priceBottom = 282
      const volumeTop = 316; const volumeBottom = 378
      const bars = this.displayBars
      const valid = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0
      const prices = bars.flatMap(bar => [bar.low, bar.high, ...MA_STYLES.map(ma => bar[`ma${ma.period}`]),
        ...(this.showRepair ? REPAIR_STYLES.map(line => (bar.bottom_repair || {})[line.key]) : [])]).concat(this.chanShapes.lines.flatMap(line => [line.p1, line.p2]), this.chanShapes.centers.flatMap(c => [c.low, c.high]), this.chanShapes.marks.map(m => m.price)).filter(valid).map(Number)
      const low = prices.length ? Math.min(...prices) : 0
      const high = prices.length ? Math.max(...prices) : 1
      const padding = Math.max((high - low) * 0.07, high * 0.003, 0.01)
      const min = Math.max(0, low - padding); const max = high + padding
      const y = value => top + (max - Number(value)) * (priceBottom - top) / (max - min)
      const step = (right - left) / Math.max(bars.length, 1)
      const x = index => left + step * (index + 0.5)
      const maxVolume = Math.max(...bars.map(bar => Number(bar.volume) || 0), 1)
      const candles = bars.map((bar, index) => {
        const open = y(bar.open); const close = y(bar.close)
        const volumeHeight = Math.max(0, Number(bar.volume) || 0) / maxVolume * (volumeBottom - volumeTop)
        return { date: bar.trade_date, x: x(index), high: y(bar.high), low: y(bar.low), close,
          bodyY: Math.min(open, close), bodyHeight: Math.max(Math.abs(open - close), 1),
          volumeY: volumeBottom - volumeHeight, volumeHeight, color: Number(bar.close) >= Number(bar.open) ? '#c34e47' : '#1d8b63' }
      })
      const maLines = MA_STYLES.map(ma => {
        const segments = []; let segment = []
        bars.forEach((bar, index) => {
          const value = bar[`ma${ma.period}`]
          if (valid(value)) segment.push(`${x(index)},${y(value)}`)
          else if (segment.length) { segments.push(segment.join(' ')); segment = [] }
        })
        if (segment.length) segments.push(segment.join(' '))
        return { ...ma, segments }
      })
      const ticks = Array.from({ length: 5 }, (_, index) => {
        const value = max - (max - min) * index / 4
        return { value, y: y(value) }
      })
      const dates = [...new Set([0, Math.floor((bars.length - 1) / 3), Math.floor((bars.length - 1) * 2 / 3), bars.length - 1])]
        .filter(index => index >= 0 && bars[index]).map(index => ({ index, x: x(index), label: bars[index].trade_date,
          anchor: index === 0 ? 'start' : index === bars.length - 1 ? 'end' : 'middle' }))
      const repairLines = this.showRepair ? REPAIR_STYLES.map(line => {
        const segments = []; let segment = []; let previousStructure = null
        bars.forEach((bar, index) => {
          const repair = bar.bottom_repair || {}
          const structure = `${repair.structure_id || ''}:${line.key === 'fan_05' ? repair.fan_low_date || '' : ''}`
          if (structure !== previousStructure || !valid(repair[line.key])) {
            if (segment.length) segments.push(segment.join(' '))
            segment = []
          }
          if (valid(repair[line.key])) segment.push(`${x(index)},${y(repair[line.key])}`)
          previousStructure = structure
        })
        if (segment.length) segments.push(segment.join(' '))
        return { ...line, segments }
      }) : []
      const chanLines = this.chanShapes.lines.map(line => ({ ...line, x1: x(line.a), x2: x(line.b), y1: y(line.p1), y2: y(line.p2) }))
      const chanCenters = this.chanShapes.centers.filter(c => c.b >= c.a).map(c => ({ ...c, x: x(c.a) - step / 2, y: y(c.high), width: (c.b - c.a + 1) * step, height: Math.max(1, y(c.low) - y(c.high)) }))
      const chanMarks = this.chanShapes.marks.map(m => ({ ...m, x: x(m.i), y: y(m.price) }))
      // Labels point to true endpoints only. A line clipped by the viewport
      // must not gain a fictitious endpoint label at the clipping boundary.
      const endpoints = chanLines.filter(line => bars[line.b] && bars[line.b].trade_date === line.end_date)
        .map(line => ({ key: line.key, x: line.x2, y: line.y2, price: line.p2, label: line.kind === 'segment' ? '段' : '笔', color: line.kind === 'segment' ? '#a74779' : '#147d76' }))
      const candidates = [...chanMarks.slice(-8).map(mark => ({ ...mark, color: mark.buy ? '#c34e47' : '#287eab', label: mark.label || '分型' })), ...endpoints.slice(-8).reverse()]
      const pointLabels = []; const occupied = []
      if (this.showPointLabels) for (const point of candidates) {
        if (pointLabels.some(other => other.x === point.x && Math.abs(other.y - point.y) < 1)) continue
        const text = `${point.label} ${this.pointPrice(point.price)}`
        const labelWidth = [...text].reduce((sum, ch) => sum + (ch.charCodeAt(0) > 255 ? 10 : 6), 0)
        for (const offset of [-10, 22, -28, 40]) {
          const labelX = Math.max(left + 2, Math.min(right - labelWidth - 2, point.x + 5))
          const labelY = Math.max(top + 12, Math.min(priceBottom - 2, point.y + offset))
          const box = { x: labelX, y: labelY - 11, w: labelWidth, h: 14 }
          if (occupied.some(old => box.x < old.x + old.w + 3 && box.x + box.w + 3 > old.x && box.y < old.y + old.h && box.y + box.h > old.y)) continue
          occupied.push(box); pointLabels.push({ ...point, text, labelX, labelY }); break
        }
        if (pointLabels.length >= 12) break
      }
      const selectedPrice = bars.length && valid(this.activeBar.close) ? { y: y(this.activeBar.close) } : null
      const referenceLines = this.showPointLabels && this.scenarioModel ? (this.scenarioModel.chart_levels || [])
        .filter(ref => valid(ref.price) && Number(ref.price) >= min && Number(ref.price) <= max)
        .map(ref => ({ ...ref, y: y(ref.price), color: ref.role === 'support' ? '#147d76' : ref.role === 'resistance' ? '#a74779' : '#287eab' }))
        .sort((a, b) => a.y - b.y) : []
      let lastLabelY = top
      for (const ref of referenceLines) {
        ref.labelY = Math.max(ref.y - 4, lastLabelY + 14, top + 12)
        lastLabelY = ref.labelY
      }
      if (referenceLines.length && lastLabelY > priceBottom - 4) {
        let next = priceBottom - 4
        for (const ref of [...referenceLines].reverse()) { ref.labelY = Math.min(ref.labelY, next); next = ref.labelY - 14 }
      }
      const repairIndex = this.activeIndex >= 0 && bars[this.activeIndex] ? this.activeIndex : bars.length - 1
      const repairLabels = hasRepairLabels ? this.repairReadouts.filter(line => line.value !== null)
        .map(line => ({ ...line, x: x(repairIndex), y: y(line.value), labelX: right + 18 }))
        .sort((a, b) => a.y - b.y) : []
      let repairLabelY = top - 36
      for (const label of repairLabels) {
        label.labelY = Math.max(top + 16, label.y - 5, repairLabelY + 46)
        repairLabelY = label.labelY
      }
      if (repairLabels.length && repairLabelY > priceBottom - 24) {
        let next = priceBottom - 24
        for (const label of [...repairLabels].reverse()) { label.labelY = Math.min(label.labelY, next); next = label.labelY - 46 }
      }
      return { width, height, left, right, top, volumeTop, volumeBottom, step, candleWidth: Math.max(1, Math.min(step * 0.65, 11)), candles, maLines, repairLines, repairLabels, chanLines, chanCenters, chanMarks, pointLabels, referenceLines, selectedPrice, ticks, dates }
    }
  },
  watch: {
    isOpen (open) { document.body.classList.toggle('stock-chart-open', open) }
  },
  beforeDestroy () {
    document.body.classList.remove('stock-chart-open')
    this.cancelSearch()
    this.cancelKline()
    this.cancelChan()
  },
  methods: {
    pointPrice (value) { return `${this.price(value)}${this.stock.isIndex ? '点' : '元'}` },
    signalLabel (signal) { const names = { '1': '一', '1p': '类一', '2': '二', '2s': '类二', '3a': '三A', '3b': '三B' }; return `${names[signal.type] || signal.type || ''}${signal.side === 'buy' ? '买' : '卖'}` },
    cancelChan () {
      this.chanRequestId += 1
      if (this.chanController) this.chanController.abort()
      if (this.chanTimer) window.clearTimeout(this.chanTimer)
      this.chanController = null; this.chanTimer = null; this.chanLoading = false
    },
    async loadChan () {
      this.cancelChan()
      if (!this.isOpen || !this.stock.code) return
      const requestId = this.chanRequestId; const controller = new window.AbortController()
      this.chanController = controller; this.chanLoading = true; this.chanError = ''
      const timeout = window.setTimeout(() => controller.abort(), 15000)
      try {
        const query = this.period === 'day' ? '' : `?level=${this.period}`
        const response = await fetch(`${API_BASE}/api/v1/${this.stock.isIndex ? 'indices' : 'stocks'}/${this.stock.code}/chanlun${query}`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        const payload = await this.responseJson(response, '缠论读取失败')
        if (!response.ok) throw new Error(this.responseError(payload, '缠论读取失败'))
        if (requestId !== this.chanRequestId || !this.isOpen) return
        this.chan = payload
        if (payload.status === 'pending' && this.chanPollCount++ < 6) this.chanTimer = window.setTimeout(() => this.loadChan(), 5000)
      } catch (error) {
        if (requestId === this.chanRequestId) this.chanError = error.name === 'AbortError' ? '缠论读取超时，请重试' : error.message
      } finally {
        window.clearTimeout(timeout)
        if (requestId === this.chanRequestId) { this.chanLoading = false; this.chanController = null }
      }
    },
    maHint (period) {
      const state = (this.activeBar.ma_status || {})[`ma${period}`]
      if (!state) return `MA${period}：${period}根${this.periodLabel}收盘价的简单平均`
      return state.status === 'ok' ? `MA${period}：${period}根完整${this.periodLabel}` : `MA${period}待补：连续${state.samples}/${state.required}根${state.reset_reason ? `（${this.resetLabel(state.reset_reason)}）` : ''}`
    },
    resetLabel (reason) { return ({ price_basis_changed: '复权口径不同或未注明', unverified_source_transition: '来源价量口径尚未核验', raw_reference_discontinuity: '前收盘参考价不连续，等待复权因子', adjustment_factor_missing: '复权因子缺失，等待补齐', adjustment_factor_mismatch: '复权因子与参考价不匹配，等待核验', missing_session: '交易日日线缺口', invalid_price_or_volume: '日线价量缺失或无效' })[reason] || '历史已分段' },
    responseError (payload, fallback) {
      const detail = payload && payload.detail
      return (typeof detail === 'string' ? detail : detail && detail.message) || (payload && payload.message) || fallback
    },
    async responseJson (response, fallback) {
      // Read the body once so a legacy/incorrect proxy response such as
      // `404 page not found` does not surface as a JSON parser exception.
      if (!response || typeof response.text !== 'function') return response.json()
      const text = await response.text()
      try {
        return text.trim() ? JSON.parse(text) : {}
      } catch (error) {
        const status = response.status ? `HTTP ${response.status}` : 'HTTP未知'
        const detail = response.status === 404
          ? '接口不存在，请检查 Nginx 的 /api/v1/stocks/ 转发及看板后端版本'
          : '接口返回了非 JSON 内容，请检查反向代理配置'
        throw new Error(`${fallback}（${status}）：${detail}`)
      }
    },
    price (value) {
      return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '--'
    },
    percent (value) {
      if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '--'
      return `${Number(value) > 0 ? '+' : ''}${Number(value).toFixed(2)}%`
    },
    codeFromInput (value) {
      const match = String(value || '').trim().toUpperCase().match(/^(?:(?:SH|SZ|BJ)\s*)?(\d{6})(?:\.(?:SH|SZ|BJ))?$/)
      return match ? match[1] : ''
    },
    cancelSearch () {
      this.searchRequestId += 1
      if (this.searchTimer) window.clearTimeout(this.searchTimer)
      if (this.searchController) this.searchController.abort()
      this.searchController = null; this.searchTimer = null; this.searchLoading = false
    },
    onSearchInput () {
      this.cancelSearch()
      this.searchError = ''; this.suggestions = []; this.suggestionIndex = -1; this.showSuggestions = true
      if (!this.query) return
      this.searchTimer = window.setTimeout(() => { this.searchTimer = null; this.fetchSuggestions() }, 240)
    },
    async fetchSuggestions () {
      this.cancelSearch()
      const query = this.query.trim()
      if (!query) return []
      const requestId = this.searchRequestId
      const controller = new window.AbortController()
      this.searchController = controller; this.searchLoading = true; this.searchError = ''
      const timeout = window.setTimeout(() => controller.abort(), 10000)
      try {
        const response = await fetch(`${API_BASE}/api/v1/stocks/search?q=${encodeURIComponent(query)}&limit=10`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        const payload = await this.responseJson(response, '股票检索失败')
        if (response.status === 404) throw new Error('股票检索接口不存在，请检查 Nginx 的 /api/v1/stocks/ 转发及看板后端版本')
        if (!response.ok) throw new Error(this.responseError(payload, '股票检索暂不可用'))
        if (requestId !== this.searchRequestId) return []
        this.suggestions = Array.isArray(payload.items) ? payload.items : []
        this.suggestionIndex = -1
        if (!this.suggestions.length) this.searchError = '未找到匹配股票，可直接输入六位代码查询已入库日K。'
        return this.suggestions
      } catch (error) {
        if (requestId === this.searchRequestId) this.searchError = error.name === 'AbortError' ? '股票检索超时，请重试。' : error.message
        return []
      } finally {
        window.clearTimeout(timeout)
        if (requestId === this.searchRequestId) { this.searchLoading = false; this.searchController = null }
      }
    },
    moveSuggestion (direction) {
      this.showSuggestions = true
      const count = this.suggestions.length
      if (count) this.suggestionIndex = this.suggestionIndex < 0
        ? (direction > 0 ? 0 : count - 1)
        : (this.suggestionIndex + direction + count) % count
    },
    onSearchBlur (event) {
      if (!event.currentTarget.contains(event.relatedTarget)) this.showSuggestions = false
    },
    chooseSuggestion (stock) {
      this.cancelSearch(); this.showSuggestions = false
      this.query = stock.name || stock.code
      return this.openStock(stock)
    },
    async submitSearch () {
      const code = this.codeFromInput(this.query)
      if (code) return this.chooseSuggestion(this.suggestions.find(item => item.code === code) || { code })
      if (this.suggestionIndex >= 0 && this.suggestions[this.suggestionIndex]) return this.chooseSuggestion(this.suggestions[this.suggestionIndex])
      const query = this.query.trim()
      if (!query) { this.searchError = '请输入股票代码或名称。'; return }
      const items = await this.fetchSuggestions()
      if (this.query.trim() !== query) return
      const exact = items.filter(item => item.name === query)
      if (exact.length === 1 || items.length === 1) return this.chooseSuggestion(exact[0] || items[0])
      this.showSuggestions = true
      if (items.length > 1) this.searchError = '匹配到多只股票，请从联想列表选择。'
    },
    cancelKline () {
      this.klineRequestId += 1
      if (this.klineController) this.klineController.abort()
      if (this.klineTimer) window.clearTimeout(this.klineTimer)
      this.klineTimer = null
      this.klineController = null
    },
    changePeriod (level) {
      if (level === this.period || !this.periodOptions.some(option => option.value === level)) return
      return this.openStock({ ...this.stock, level })
    },
    async openStock (stock, { background = false } = {}) {
      const code = this.codeFromInput(stock && stock.code)
      if (!code) { this.searchError = '请输入六位股票代码或选择联想结果。'; return }
      // Search results use market for the exchange (sh/sz/bj), not security type.
      // Keep the explicit index marker through retries and both chart requests.
      const level = stock.level || this.period
      if (!this.periodOptions.some(option => option.value === level)) return
      const target = { code, name: stock.name || '', isIndex: stock.isIndex === true, level }
      if (!this.isOpen) this.returnFocus = document.activeElement
      this.cancelSearch(); this.showSuggestions = false; this.cancelKline(); this.cancelChan()
      const requestId = this.klineRequestId
      const controller = new window.AbortController()
      this.klineController = controller
      this.stock = target; this.period = level; this.isOpen = true; this.loading = !background
      this.chan = {}; this.chanError = ''; this.chanPollCount = 0
      this.error = ''; this.activeIndex = -1
      if (!background) { this.bars = []; this.result = {} }
      this.$nextTick(() => { if (!background && this.isOpen && this.$refs.closeButton) this.$refs.closeButton.focus() })
      const timeout = window.setTimeout(() => controller.abort(), 15000)
      try {
        const route = level === 'day' ? 'daily-kline?limit=250' : `kline?level=${level}&limit=250`
        const response = await fetch(`${API_BASE}/api/v1/${target.isIndex ? 'indices' : 'stocks'}/${encodeURIComponent(code)}/${route}`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        const payload = await this.responseJson(response, '日K读取失败')
        if (response.status === 404) throw new Error('日K接口不存在，请检查 Nginx 的 /api/v1/stocks/ 转发及看板后端版本')
        if (!response.ok) throw new Error(this.responseError(payload, '日K读取失败'))
        if (requestId !== this.klineRequestId) return
        this.result = payload
        this.bars = Array.isArray(payload.bars) ? payload.bars : []
        this.stock = { ...target, name: payload.name || target.name }
        if (payload.status === 'pending' || level.endsWith('m')) {
          const delay = payload.status === 'pending' ? 5000 : 60000
          this.klineTimer = window.setTimeout(() => this.openStock(this.stock, { background: this.bars.length > 0 }), delay)
        }
        // Schedule independently; a missing Chan runtime must not block MA/K-line.
        this.chanTimer = window.setTimeout(() => this.loadChan(), 0)
      } catch (error) {
        if (requestId === this.klineRequestId) this.error = error.name === 'AbortError' ? '日K查询超时，请重试。' : error.message
      } finally {
        window.clearTimeout(timeout)
        if (requestId === this.klineRequestId) {
          this.loading = false; this.klineController = null
          if (!background && this.bars.length) this.scrollToLatest()
        }
      }
    },
    close () {
      this.cancelKline(); this.cancelChan(); this.isOpen = false; this.loading = false
      this.$nextTick(() => { if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus() })
    },
    onDialogKeydown (event) {
      if (event.key === 'Escape') { event.stopPropagation(); this.close(); return }
      if (event.key !== 'Tab') return
      const nodes = [...this.$refs.dialog.querySelectorAll('button:not([disabled]), [tabindex="0"]')]
      const first = nodes[0]; const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    },
    scrollToLatest () {
      this.$nextTick(() => {
        const chart = this.$refs.chartScroll
        if (this.isOpen && chart) chart.scrollLeft = Math.max(0, chart.scrollWidth - chart.clientWidth)
      })
    },
    setRange (count) { this.visibleCount = count; this.activeIndex = -1; this.scrollToLatest() },
    moveBar (direction) {
      const length = this.displayBars.length
      if (!length) return
      const current = this.activeIndex < 0 ? length - 1 : this.activeIndex
      this.activeIndex = Math.max(0, Math.min(length - 1, current + direction))
    }
  }
}
</script>

<style scoped>
.stock-chart-lookup { margin-bottom: 14px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; }
.stock-chart-periods { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; padding: 10px 18px; border-bottom: 1px solid var(--hairline); }
.stock-chart-periods button { min-height: 36px; padding: 5px 10px; background: transparent; border: 1px solid var(--hairline); border-radius: 4px; color: var(--ink); cursor: pointer; }
.stock-chart-periods button.active { color: var(--coral); border-color: var(--coral); }
.stock-chart-periods small { font-size: 11px; color: var(--muted); }
.stock-chart-point-label text { font-size: 10px; paint-order: stroke; stroke: #fffaf0; stroke-width: 3px; stroke-linejoin: round; }
.stock-chart-center-label { font-size: 10px; fill: #287eab; paint-order: stroke; stroke: #fffaf0; stroke-width: 3px; }
@media (max-width: 600px) { .stock-chart-periods button { min-height: 44px; } }
.stock-chart-lookup-label { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 44px; padding: 10px 14px; cursor: pointer; font-size: 12px; list-style: none; }
.stock-chart-lookup-label::-webkit-details-marker { display: none; }
.stock-chart-lookup-disclosure[open] .stock-chart-lookup-label { border-bottom: 1px solid var(--hairline); }
.stock-chart-lookup-label span { color: var(--muted); font-size: 11px; }
.stock-chart-search { position: relative; width: min(calc(100% - 28px), 540px); margin: 12px 14px; }
.stock-chart-lookup.compact { flex: 0 0 auto; margin: 0; }
.compact .stock-chart-lookup-label { min-height: 38px; padding: 8px 11px; font-size: 12px; }
.compact .stock-chart-search { position: absolute; z-index: 25; top: calc(100% + 8px); left: 0; width: 100%; margin: 0; padding: 12px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; box-shadow: var(--shadow); }
.compact .stock-chart-suggestions { right: 12px; left: 12px; }
.stock-chart-search-form { display: flex; border: 1px solid var(--hairline); border-radius: 5px; overflow: hidden; }
.stock-chart-search-form input { min-width: 0; flex: 1; padding: 9px 11px; border: none; outline: none; color: var(--ink); background: transparent; font-size: 12px; }
.stock-chart-search-form:focus-within { border-color: var(--coral); }
.stock-chart-search-form button { padding: 9px 12px; border: none; background: var(--dark); color: #fff; font-size: 12px; white-space: nowrap; }
.stock-chart-suggestions { position: absolute; z-index: 12; top: 100%; left: 0; right: 0; margin: 4px 0 0; padding: 4px; list-style: none; background: var(--paper); border: 1px solid var(--hairline); box-shadow: var(--shadow); border-radius: 5px; }
.stock-chart-suggestions button { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px; text-align: left; border: none; border-radius: 3px; background: transparent; color: var(--ink); }
.stock-chart-suggestions button:hover, .stock-chart-suggestions button.active { background: var(--cream); }
.stock-chart-suggestions strong { font-size: 12px; }
.stock-chart-suggestions code, .stock-chart-suggestions small { color: var(--muted); font-size: 11px; }
.stock-chart-suggestions small { margin-left: auto; max-width: 40%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stock-chart-search-note { margin: 6px 0 0; font-size: 11px; color: var(--coral); }
.stock-chart-overlay { position: fixed; z-index: 150; inset: 0; display: grid; place-items: center; padding: 24px; overflow-y: auto; background: rgba(14, 23, 34, .62); overscroll-behavior: contain; }
.stock-chart-dialog { width: min(1100px, 100%); max-height: calc(100dvh - 48px); padding: 24px 28px 20px; overflow-y: auto; border: 1px solid var(--hairline); border-radius: 12px; background: var(--paper); box-shadow: 0 24px 90px rgba(0,0,0,.24); }
.stock-chart-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.stock-chart-header p { margin: 0 0 6px; font-size: 10px; letter-spacing: .12em; color: var(--coral); }
.stock-chart-header h3 { margin: 0; font-size: 24px; font-weight: 600; }
.stock-chart-header code { margin-left: 8px; font-size: 14px; font-weight: 400; color: var(--muted); }
.stock-chart-close { width: 34px; height: 34px; flex-shrink: 0; border: 1px solid var(--hairline); border-radius: 50%; color: var(--ink); background: transparent; font-size: 25px; line-height: 1; }
.stock-chart-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 14px; font-size: 13px; }
.stock-chart-toolbar small { color: var(--muted); margin-left: 8px; font-size: 11px; }
.stock-chart-ranges { display: flex; gap: 4px; }
.stock-chart-ranges button { padding: 5px 9px; border: 1px solid var(--hairline); border-radius: 4px; background: transparent; color: var(--muted); font-size: 11px; }
.stock-chart-ranges button.active { background: var(--dark); color: #fff; border-color: var(--dark); }
.stock-chart-ohlc { display: flex; flex-wrap: wrap; gap: 8px 22px; font-size: 12px; color: var(--muted); }
.stock-chart-ohlc b { margin-left: 5px; font-variant-numeric: tabular-nums; }
.stock-chart-up { color: #c34e47; }.stock-chart-down { color: #1d8b63; }
.stock-chart-ma-values { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 16px 0; }
.stock-chart-ma-values div { padding: 9px 12px; border: 1px solid var(--hairline); border-top: 2px solid var(--ma-color); border-radius: 4px; color: var(--ma-color); }
.stock-chart-ma-values span { display: block; font-size: 11px; }
.stock-chart-ma-values strong { display: block; margin-top: 4px; font-size: 17px; font-weight: 500; font-variant-numeric: tabular-nums; }
.stock-chart-scroll { overflow-x: auto; border: 1px solid var(--hairline); border-radius: 5px; }
.stock-chan-scenario { margin: 12px 0; padding: 12px; border: 1px solid var(--hairline); border-radius: 5px; font-size: 12px; }
.stock-chan-scenario header { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.stock-chan-scenario p { margin: 6px 0; line-height: 1.6; }
.stock-chan-scenario small, .stock-chan-references span { color: var(--muted); font-size: 11px; overflow-wrap: anywhere; }
.stock-chan-references { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; margin: 12px 0; }
.stock-chan-references p { font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.stock-chan-references small { display: block; }
.stock-chan-conditions { margin: 10px 0; }
.stock-chan-conditions summary { cursor: pointer; min-height: 32px; }
.stock-chan-conditions article { padding: 10px; margin: 6px 0; border-left: 2px solid var(--hairline); }
.stock-chan-conditions .is-current { border-left-color: var(--coral); background: var(--paper); }
.stock-chan-hint { font-size: 12px; color: var(--muted); line-height: 1.6; overflow-wrap: anywhere; }
.stock-chan-hint button { min-height: 36px; border: 1px solid var(--hairline); background: transparent; color: var(--ink); border-radius: 4px; }
.stock-chart-reference text { font-size: 10px; paint-order: stroke; stroke: #fffaf0; stroke-width: 3px; stroke-linejoin: round; }
.stock-chart-repair-label text { font-size: 14px; font-weight: 500; font-variant-numeric: tabular-nums; }
.stock-chart-repair-label .stock-chart-repair-meaning { font-size: 12px; font-weight: 400; fill: var(--muted); }
.stock-chart-svg { display: block; width: 100%; min-width: 640px; }
.stock-chart-grid line { stroke: var(--hairline); stroke-dasharray: 3 4; stroke-width: .7; }
.stock-chart-grid text, .stock-chart-axis { fill: var(--muted); font-size: 10px; }
.stock-chart-crosshair { stroke: var(--muted); stroke-width: 1; stroke-dasharray: 4 4; pointer-events: none; }
.stock-repair-detail { margin-bottom: 12px; padding: 12px; border: 1px solid var(--hairline); border-radius: 5px; font-size: 11px; }
.stock-repair-detail summary { display: flex; flex-wrap: wrap; gap: 8px 14px; cursor: pointer; }
.stock-repair-detail small, .stock-repair-detail p { color: var(--muted); line-height: 1.6; overflow-wrap: anywhere; }
.stock-repair-values { display: grid; grid-template-columns: repeat(auto-fit,minmax(140px,1fr)); gap: 12px; margin-top: 16px; }
.stock-repair-values span, .stock-repair-values strong { display: block; margin-bottom: 5px; }
.stock-repair-values span { color: var(--muted); }
.stock-repair-evidence { display: grid; gap: 8px; margin: 14px 0; padding: 0; list-style: none; }
.stock-repair-evidence li { display: flex; gap: 12px; flex-wrap: wrap; }
.stock-repair-evidence small { margin-left: auto; }
.stock-repair-toggle { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 10px 0; color: var(--muted); }
.stock-repair-toggle button { min-height: 36px; padding: 6px 10px; color: var(--ink); background: transparent; border: 1px solid var(--hairline); border-radius: 4px; font-size: 11px; }
.stock-repair-empty { color: var(--muted); font-size: 12px; line-height: 1.6; }
.stock-chart-footer { display: grid; gap: 5px; padding-top: 14px; color: var(--muted); font-size: 11px; line-height: 1.5; }
.stock-chan-detail button { min-height: 36px; padding: 5px 10px; border: 1px solid var(--hairline); border-radius: 4px; background: transparent; color: var(--ink); }
.stock-chan-layers { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 10px; }
.stock-chan-layers label { display: inline-flex; align-items: center; gap: 5px; min-height: 36px; cursor: pointer; }
.stock-chan-signals { padding-left: 16px; color: var(--muted); line-height: 1.8; overflow-wrap: anywhere; }
.stock-chart-message { min-height: 200px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 12px; color: var(--muted); font-size: 13px; }
.stock-chart-message button { padding: 5px 12px; color: var(--coral); background: transparent; border: 1px solid var(--hairline); border-radius: 4px; }
@media (max-width: 700px) {
  .compact .stock-chart-lookup-label { min-height: 44px; }
  .stock-chart-search { width: calc(100% - 28px); }
  .stock-chart-search-form input { font-size: 16px; min-height: 44px; }
  .stock-chart-overlay { padding: 8px; }.stock-chart-dialog { padding: 16px 12px; max-height: calc(100dvh - 16px); }
  .stock-chart-header h3 { font-size: 21px; }.stock-chart-header code { font-size: 12px; }
  .stock-chart-toolbar { align-items: flex-start; flex-direction: column; gap: 10px; }
  .stock-chart-ma-values { gap: 4px; }.stock-chart-ma-values div { padding: 7px 5px; }.stock-chart-ma-values strong { font-size: 14px; }
  .stock-chart-ohlc { gap: 8px 12px; }
  .stock-chart-svg.has-repair-analysis { min-width: 800px; }
}
</style>
